import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchUserLogins, UserLoginsParams } from '../api/userLogins';

export const UserLogins: React.FC = () => {
  const [filters, setFilters] = useState<UserLoginsParams>({
    limit: 50,
    offset: 0,
    sortBy: 'logged_in_at',
    sortOrder: 'desc'
  });
  const [userIdFilter, setUserIdFilter] = useState<string>('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userLogins', filters],
    queryFn: () => fetchUserLogins(filters),
  });

  const handleSort = (column: 'user_id' | 'logged_in_at' | 'first_name' | 'last_name' | 'email') => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      offset: 0
    }));
  };

  const handleUserIdFilter = () => {
    const userId = userIdFilter.trim() ? parseInt(userIdFilter, 10) : undefined;
    setFilters(prev => ({
      ...prev,
      userId,
      offset: 0
    }));
  };

  const handleClearFilters = () => {
    setUserIdFilter('');
    setFilters({
      limit: 50,
      offset: 0,
      sortBy: 'logged_in_at',
      sortOrder: 'desc'
    });
  };

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({
      ...prev,
      offset: newOffset
    }));
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatUserAgent = (userAgent?: string) => {
    if (!userAgent) return 'N/A';
    // Extract browser info from user agent
    const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
    return browserMatch ? browserMatch[0] : userAgent.substring(0, 50) + '...';
  };

  if (isLoading) {
    return (
      <div className="user-logins">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading user logins...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-logins">
        <div className="error-container">
          <h2>Error Loading User Logins</h2>
          <p>{error instanceof Error ? error.message : 'Failed to load user logins'}</p>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-logins">
      <header className="logins-header">
        <div className="header-left">
          <h1>User Logins</h1>
          <p>Monitor user login activity and security events</p>
        </div>
        <div className="header-right">
          <button onClick={() => refetch()} className="refresh-button">
            Refresh
          </button>
        </div>
      </header>

      <div className="logins-content">
        {/* Filters */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="userIdFilter">Filter by User ID:</label>
              <input
                id="userIdFilter"
                type="number"
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
                placeholder="Enter user ID"
                className="filter-input"
              />
              <button onClick={handleUserIdFilter} className="filter-button">
                Apply
              </button>
            </div>
            <button onClick={handleClearFilters} className="clear-filters-button">
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          <p>
            Showing {data?.logins.length || 0} of {data?.pagination.total || 0} login records
            {filters.userId && ` for User ID ${filters.userId}`}
          </p>
        </div>

        {/* Table */}
        <div className="logins-table-container">
          <table className="logins-table">
            <thead>
              <tr>
                <th 
                  className={`sortable ${filters.sortBy === 'user_id' ? `sorted-${filters.sortOrder}` : ''}`}
                  onClick={() => handleSort('user_id')}
                >
                  User ID
                  <span className="sort-indicator">
                    {filters.sortBy === 'user_id' ? (filters.sortOrder === 'desc' ? '↓' : '↑') : '↕'}
                  </span>
                </th>
                <th 
                  className={`sortable ${filters.sortBy === 'first_name' ? `sorted-${filters.sortOrder}` : ''}`}
                  onClick={() => handleSort('first_name')}
                >
                  Name
                  <span className="sort-indicator">
                    {filters.sortBy === 'first_name' ? (filters.sortOrder === 'desc' ? '↓' : '↑') : '↕'}
                  </span>
                </th>
                <th>Email</th>
                <th 
                  className={`sortable ${filters.sortBy === 'logged_in_at' ? `sorted-${filters.sortOrder}` : ''}`}
                  onClick={() => handleSort('logged_in_at')}
                >
                  Login Date
                  <span className="sort-indicator">
                    {filters.sortBy === 'logged_in_at' ? (filters.sortOrder === 'desc' ? '↓' : '↑') : '↕'}
                  </span>
                </th>
                <th>IP Address</th>
                <th>Browser</th>
              </tr>
            </thead>
            <tbody>
              {data?.logins.map((login) => (
                <tr key={login.id}>
                  <td className="user-id-cell">{login.user_id}</td>
                  <td className="name-cell">
                    {login.first_name} {login.last_name}
                  </td>
                  <td className="email-cell">{login.email}</td>
                  <td className="date-cell">{formatDateTime(login.logged_in_at)}</td>
                  <td className="ip-cell">{login.ip_address || 'N/A'}</td>
                  <td className="browser-cell" title={login.user_agent}>
                    {formatUserAgent(login.user_agent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data?.pagination && data.pagination.total > filters.limit! && (
          <div className="pagination">
            <button
              onClick={() => handlePageChange(Math.max(0, filters.offset! - filters.limit!))}
              disabled={filters.offset === 0}
              className="pagination-button"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {Math.floor(filters.offset! / filters.limit!) + 1} of {Math.ceil(data.pagination.total / filters.limit!)}
            </span>
            <button
              onClick={() => handlePageChange(filters.offset! + filters.limit!)}
              disabled={!data.pagination.hasMore}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
